import { Box, useTheme, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode, MouseEventHandler } from 'react';

type IconColorKey = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

interface CustomButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'icon';
  color?: IconColorKey;
  disabled?: boolean;
  customSize?: { width?: number | string; height?: number | string };
  icon?: ReactNode;
  iconColor?: string;
  iconPosition?: 'left' | 'only';
  endIcon?: ReactNode;
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  form?: string;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

const COLOR_KEY_MAP: Record<IconColorKey, keyof Theme['palette']> = {
  primary: 'primary',
  secondary: 'secondary',
  error: 'error',
  info: 'info',
  success: 'success',
  warning: 'warning',
};

function resolveIconColor(theme: Theme, iconColor?: string): string | undefined {
  if (!iconColor) return undefined;
  const key = iconColor as IconColorKey;
  if (key in COLOR_KEY_MAP) {
    const paletteKey = COLOR_KEY_MAP[key];
    return (theme.palette[paletteKey] as { main: string }).main;
  }
  return iconColor;
}

function getVariantStyles(
  theme: Theme,
  variant: NonNullable<CustomButtonProps['variant']>,
  disabled: boolean,
  color: IconColorKey = 'primary'
) {
  const { palette } = theme;
  const isDark = palette.mode === 'dark';
  
  const paletteColor = palette[COLOR_KEY_MAP[color]] as { main: string; dark: string };
  const mainColor = paletteColor.main;
  const darkColor = paletteColor.dark;

  const styles: Record<string, Record<string, unknown>> = {
    primary: {
      backgroundColor: mainColor,
      color: '#fff',
      border: 'none',
      '&:hover': {
        backgroundColor: darkColor,
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${alpha(mainColor, 0.3)}`,
      },
    },
    secondary: {
      backgroundColor: isDark
        ? alpha(palette.common.white, 0.08)
        : alpha(palette.common.black, 0.06),
      color: palette.text.primary,
      border: 'none',
      '&:hover': {
        backgroundColor: isDark
          ? alpha(palette.common.white, 0.15)
          : alpha(palette.common.black, 0.12),
      },
    },
    outline: {
      backgroundColor: 'transparent',
      color: mainColor,
      border: 1.5,
      borderColor: isDark ? alpha(palette.divider, 0.5) : alpha(palette.text.primary, 0.12),
      '&:hover': {
        backgroundColor: alpha(mainColor, isDark ? 0.15 : 0.08),
        borderColor: darkColor,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: color !== 'primary' ? mainColor : palette.text.secondary,
      border: 'none',
      '&:hover': {
        backgroundColor: alpha(mainColor, isDark ? 0.15 : 0.08),
        color: color !== 'primary' ? darkColor : palette.text.primary,
      },
    },
    icon: {
      backgroundColor: 'transparent',
      color: 'inherit',
      border: 'none',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  };

  const variantStyle = styles[variant] ?? styles.primary;

  return {
    ...variantStyle,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    pointerEvents: disabled ? 'none' : ('auto' as React.CSSProperties['pointerEvents']),
  };
}

export default function CustomButton({
  variant = 'primary',
  disabled = false,
  color = 'primary',
  customSize,
  icon,
  iconColor,
  iconPosition = 'left',
  endIcon,
  children,
  onClick,
  type = 'button',
  fullWidth = false,
  form,
  sx,
  'aria-label': ariaLabel,
}: CustomButtonProps) {
  const theme = useTheme();
  const variantSx = getVariantStyles(theme, variant, disabled, color);

  const isIconOnly = variant === 'icon' || (icon && iconPosition === 'only' && !children);
  const hasIconLeft = icon && iconPosition === 'left';

  const resolvedIconColor = resolveIconColor(theme, iconColor) ?? (variant === 'icon' ? theme.palette.text.secondary : undefined);

  const iconElement = (node: ReactNode) => node ? (
    <Box
      component="span"
      sx={{
        display: 'flex',
        color: resolvedIconColor,
        lineHeight: 0,
        ...(variant === 'icon' && {
          borderRadius: 1,
          transition: 'background-color 0.15s ease',
          width: 36,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }),
      }}
    >
      {node}
    </Box>
  ) : null;



  return (
    <Box
      component="button"
      type={type}
      form={form}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: hasIconLeft || endIcon ? 1 : 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1,
        borderRadius: 1,
        outline: 'none',
        transition: 'all 0.15s ease',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        width: fullWidth ? '100%' : (customSize?.width ?? 'auto'),
        height: customSize?.height ?? (isIconOnly ? 40 : 40),
        minWidth: isIconOnly ? (customSize?.height ?? 40) : 0,
        px: isIconOnly ? 0 : 3,
        ...(fullWidth && {
          '.MuiDialogActions-root > &:not(:first-of-type)': {
            marginLeft: 0,
          },
        }),
        ...variantSx,
        ...(sx as Record<string, unknown>),
      }}
    >
      {(hasIconLeft || isIconOnly) && iconElement(icon)}
      {!isIconOnly && children}
      {!isIconOnly && iconElement(endIcon)}
    </Box>
  );
}
